import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Sparkles,
  LucideIcon,
  UserPlus,
  Users,
  PlusCircle,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toolsList } from "@packages/utils";
import DashboardOnboarding from "./DashboardOnboarding";
import { Button } from "@core/button";
import { CreateClassroomModal } from "../../students/components/CreateClassroomModal";
import AddStudentModal from "../../students/components/AddStudentModal";
import { Text, ToolCard } from "../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useAppSelector, useAppDispatch } from "@packages/store/hooks";
import {
  getCurrentTeacher,
  toggleFavoriteTool,
} from "@packages/store/userSlice";
import astroMadlen from "@packages/assets/astromadlen.png";
import RecentlyAddedItem from "./DashboardRecentlyAdded";
import AddFavoritesModal from "./DashboardAddFavoritesModal";

interface ActionItem {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  color: string;
  disabled: boolean;
  isNew?: boolean;
  isFavorite?: boolean;
  canBeFavorite?: boolean;
}

const ModernActionCard = ({
  action,
  index,
}: {
  action: ActionItem;
  index: number;
}) => {
  const { trackEvent: track } = trackEvent();
  const dispatch = useAppDispatch();

  const handleClick = () => {
    track(
      {
        category: "ContentClick",
        action: "dashboard.quick.action.click",
        label: "Dashboard Quick Action Click",
        nonInteraction: false,
      },
      {
        location: "/dashboard/click",
        element: "click-button",
      },
    );
    action.onClick();
  };

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(toggleFavoriteTool(action.id));
    dispatch(getCurrentTeacher());
  };

  return (
    <ToolCard
      id={action.id}
      title={action.title}
      description={action.description}
      icon={action.icon}
      iconBgColor={action.color}
      onClick={handleClick}
      onToggleFavorite={action.canBeFavorite ? toggleFavorite : undefined}
      isFavorite={action.isFavorite}
      isNew={action.isNew}
      disabled={action.disabled}
      index={index}
    />
  );
};

const EmptyStateMessage = ({
  title,
  description,
  showAddButton = false,
}: {
  title: string;
  description: string;
  showAddButton?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="col-span-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="bg-gray-50/50 dark:bg-dark-500 rounded-xl p-8 text-center border border-gray-100 dark:border-dark-400">
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-base font-medium text-gray-600 dark:text-gray-300">
              {t(title)}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
              {t(description)}
            </p>
          </div>
          {showAddButton && (
            <div className="pt-2 w-full flex justify-center">
              <AddFavoritesModal />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Mobile-aware DashboardOnboarding wrapper
const MobileDashboardOnboarding = ({
  children,
  videoName,
  wrapperClassName,
}: {
  children: React.ReactNode;
  videoName: string;
  wrapperClassName?: string;
}) => {
  return (
    <div className="group relative">
      {/* Desktop için normal DashboardOnboarding */}
      {/* <div className="hidden md:block">
        <DashboardOnboarding
          videoName={videoName}
          wrapperClassName={wrapperClassName}
        >
          {children}
        </DashboardOnboarding>
      </div> */}

      {/* Mobile için sadece children, onboarding yok */}
      {/* <div className="block md:hidden">{children}</div> */}

      {/* Video gösterimi kapatıldı - sadece children render ediliyor */}
      {children}
    </div>
  );
};

const SectionHeader = ({
  title,
  subtitle,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        {subtitle && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
};

const DashboardQuickActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedClassUUID, setSelectedClassUUID] = useState<string>();
  const [isCreateClassroomOpen, setIsCreateClassroomOpen] = useState(false);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [showEssayGraderModal, setShowEssayGraderModal] = useState(false);
  const teacherPreferences = useAppSelector(
    (state) => state.user.teacher?.preferences,
  );

  const favorites = teacherPreferences?.favoriteTools || [];
  const recentlyUsed = teacherPreferences?.lastUsedTools || [];

  const essentialTools = [
    {
      id: "createAssignment",
      title: t("quickActions.createAssignment"),
      description: t("quickActions.createAssignmentDesc"),
      icon: PlusCircle,
      onClick: () => navigate("/assignments"),
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
      disabled: false,
      canBeFavorite: false,
    },
    {
      id: "createClassroom",
      title: t("quickActions.createClassroom"),
      description: t("quickActions.createClassroomDesc"),
      icon: Users,
      onClick: () => setIsCreateClassroomOpen(true),
      color: "bg-gradient-to-br from-blue-500 to-blue-600",
      disabled: false,
      canBeFavorite: false,
    },
    {
      id: "addStudent",
      title: t("quickActions.addStudent"),
      description: t("quickActions.addStudentDesc"),
      icon: UserPlus,
      onClick: () => setIsAddStudentOpen(true),
      color: "bg-gradient-to-br from-pink-500 to-pink-600",
      disabled: false,
      canBeFavorite: false,
    },
  ];

  // Function to get all tools in a flat structure
  const getAllTools = () => {
    const allTools: ActionItem[] = [];
    toolsList.forEach((category) => {
      category.tools.forEach((tool) => {
        allTools.push({
          id: tool.id,
          title: t(tool.title),
          description: t(tool.description),
          icon: tool.icon,
          onClick: () => {
            if (tool.id === "essay-grader") {
              setShowEssayGraderModal(true);
            } else {
              navigate(tool.path);
            }
          },
          color: `bg-gradient-to-br ${tool.colors?.light || "from-gray-500 to-gray-600"}`,
          disabled: false,
          isFavorite: favorites.includes(tool.id),
          isNew: [
            "ai-supported-tasks",
            "blooms-taxonomy-activity",
            "ai-assignment-designer",
          ].includes(tool.id),
          canBeFavorite: true,
        });
      });
    });
    return allTools;
  };

  const allTools = useMemo(() => getAllTools(), [favorites, recentlyUsed]);

  // Update to show the most recent tools (last 4 items) first
  const recentlyUsedTools = useMemo(() => {
    if (recentlyUsed.length === 0) {
      return [];
    }

    // Create a copy of recentlyUsed array and reverse it to get most recent first
    const mostRecentFirst = [...recentlyUsed].reverse();
    // Get the last 4 used tools (most recent)
    const recentToolIds = mostRecentFirst.slice(0, 6);
    // Map them to their corresponding tool objects, preserving the order of recency
    return recentToolIds
      .map((id) => allTools.find((tool) => tool.id === id))
      .filter(Boolean) as ActionItem[];
  }, [allTools, recentlyUsed]);

  const favoriteTools =
    allTools.filter((tool) => favorites.includes(tool.id)) || [];

  const displayedFavorites = favoriteTools;

  const recentlyAddedTools = allTools.filter((tool) => tool.isNew).slice(0, 3);

  return (
    <div className="space-y-12 px-1">
      {/* Favorites Section */}
      <section>
        <SectionHeader
          title={t("quickActions.favorites")}
          subtitle="Your most-used tools for quick access"
        >
          <AddFavoritesModal />
        </SectionHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {displayedFavorites.length > 0 ? (
              displayedFavorites.map((tool, index) => (
                <MobileDashboardOnboarding key={tool.id} videoName={tool.id}>
                  <ModernActionCard action={tool} index={index} />
                </MobileDashboardOnboarding>
              ))
            ) : (
              <EmptyStateMessage
                title="quickActions.noFavorites"
                description="quickActions.addFavoritesHint"
                showAddButton={true}
              />
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Recently Used Section */}
      <section>
        <SectionHeader
          title={t("quickActions.recentlyUsed")}
          subtitle="Continue where you left off"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {recentlyUsedTools.length > 0 ? (
              recentlyUsedTools.map((tool, index) => (
                <MobileDashboardOnboarding key={tool.id} videoName={tool.id}>
                  <ModernActionCard action={tool} index={index} />
                </MobileDashboardOnboarding>
              ))
            ) : (
              <EmptyStateMessage
                title="quickActions.noRecentlyUsed"
                description="quickActions.addRecentlyUsedHint"
              />
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Essential Tools Section */}
      <section>
        <SectionHeader
          title={t("quickActions.essentialTools")}
          subtitle="Core features to get started"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {essentialTools.map((tool, index) => (
              <ModernActionCard key={tool.id} action={tool} index={index} />
            ))}
          </AnimatePresence>
        </div>
      </section>

      {/* Recently Added Section 
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-amber-500/5 rounded-3xl -m-6"></div>
        
        <div className="relative z-10 pt-6">
          <SectionHeader 
            title={
              <span className="flex items-center gap-3">
                {t("quickActions.recentlyAdded")}
                <div className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                </div>
              </span>
            }
            subtitle={
              <span className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                <Sparkles size={14} />
                {t("quickActions.freshFeaturesToExplore")}
              </span>
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {recentlyAddedTools.map((tool, index) => (
                <MobileDashboardOnboarding 
                  key={tool.id} 
                  videoName={tool.id}
                  wrapperClassName="h-full w-full"
                >
                  <ModernActionCard action={tool} index={index} />
                </MobileDashboardOnboarding>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
      */}
      <CreateClassroomModal
        show={isCreateClassroomOpen}
        onClose={() => setIsCreateClassroomOpen(false)}
        onSubmit={(newClassroom) => {
          setSelectedClassUUID(newClassroom.uuid);
          setTimeout(() => {
            setIsCreateClassroomOpen(false);
            setIsAddStudentOpen(true);
          }, 50);
        }}
      />

      <AddStudentModal
        show={isAddStudentOpen}
        onClose={() => {
          setIsAddStudentOpen(false);
          setSelectedClassUUID(undefined);
        }}
        uuid={selectedClassUUID}
      />
    </div>
  );
};

export default DashboardQuickActions;
